import {
  Building2,
  Calendar,
  Globe,
  Home,
  Image,
  Info,
  Menu,
  Phone
} from 'lucide-react';
import React, { useState, useMemo } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader } from '../components/ui/card';
import { Separator } from '../components/ui/separator';
import { CMSNavigationProvider, type CMSTabType } from '../context/CMSNavigationContext';

// Import the existing content components
import GlobalSettings from './Content/GlobalSettings.tsx';

// Import page managers
import AboutPageManager from './Content/AboutPageManager.tsx';
//import ContactsPageManager from './Content/ContactsPageManager.tsx';
import GalleryPageManager from './Content/GalleryPageManager.tsx';
import HomePageManager from './Content/HomePageManager.tsx';
import MenuPageManager from './Content/MenuPageManager.tsx';
import ReservationPageManager from './Content/ReservationPageManager.tsx';
import RooftopPageManager from './Content/RooftopPageManager.tsx';

const ContentManagement: React.FC = () => {
  const [activeTab, setActiveTab] = useState<CMSTabType>('global');

  const tabs = [
    {
      id: 'global' as CMSTabType,
      label: 'Global Settings',
      icon: Globe,
      component: GlobalSettings,
      section: 'settings'
    },
    // Page Managers Section
    {
      id: 'home' as CMSTabType,
      label: 'Home Page',
      icon: Home,
      component: HomePageManager,
      section: 'pages'
    },
    {
      id: 'menu-page' as CMSTabType,
      label: 'Menu Page',
      icon: Menu,
      component: MenuPageManager,
      section: 'pages'
    },
    {
      id: 'reservation' as CMSTabType,
      label: 'Reservation Page',
      icon: Calendar,
      component: ReservationPageManager,
      section: 'pages'
    },
    {
      id: 'gallery-page' as CMSTabType,
      label: 'Gallery Page',
      icon: Image,
      component: GalleryPageManager,
      section: 'pages'
    },
    {
      id: 'about' as CMSTabType,
      label: 'About Page',
      icon: Info,
      component: AboutPageManager,
      section: 'pages'
    },
    // {
    //   id: 'contacts' as CMSTabType,
    //   label: 'Contacts Page',
    //   icon: Phone,
    //   component: ContactsPageManager,
    //   section: 'pages'
    // },
    {
      id: 'rooftop' as CMSTabType,
      label: 'Rooftop Page',
      icon: Building2,
      component: RooftopPageManager,
      section: 'pages'
    }
  ];

  const navigationValue = useMemo(
    () => ({
      activeTab,
      navigateTo: setActiveTab,
    }),
    [activeTab]
  );

  const ActiveComponent = tabs.find(tab => tab.id === activeTab)?.component || GlobalSettings;

  return (
    <CMSNavigationProvider value={navigationValue}>
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950">
        <div className="max-w-[1600px] mx-auto p-6 space-y-6">
          {/* Header */}
          <div className="backdrop-blur-sm bg-white/80 dark:bg-slate-900/80 rounded-2xl p-8 border border-slate-200 dark:border-slate-800 shadow-sm">
            <h1 className="text-4xl font-bold bg-gradient-to-r from-slate-900 via-slate-800 to-slate-700 dark:from-white dark:via-slate-100 dark:to-slate-300 bg-clip-text text-transparent">
              Content Management
            </h1>
          </div>

          {/* Tabs */}
          <Card className="backdrop-blur-sm bg-white/90 dark:bg-slate-900/90 border-slate-200 dark:border-slate-800 shadow-lg">
            <CardHeader className="pb-4">
              <div className="space-y-6">
                {/* Global Settings */}
                <div>
                  <h3 className="text-xs font-bold text-slate-500 dark:text-slate-400 mb-3 px-2 tracking-wider">SITE SETTINGS</h3>
                  <div className="flex flex-wrap gap-2">
                    {tabs.filter(tab => tab.section === 'settings').map((tab) => {
                      const Icon = tab.icon;
                      const isActive = activeTab === tab.id;
                      
                      return (
                      <Button
                        key={tab.id}
                        variant={isActive ? "default" : "outline"}
                        size="sm"
                        onClick={() => setActiveTab(tab.id)}
                        className={`flex items-center gap-2 transition-all duration-200 ${
                          isActive 
                            ? 'bg-gradient-to-r from-slate-900 to-slate-700 dark:from-slate-100 dark:to-slate-300 text-white dark:text-slate-900 shadow-md border-0' 
                            : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100 hover:bg-slate-100 dark:hover:bg-slate-800 hover:border-slate-300 dark:hover:border-slate-600'
                        }`}
                      >
                        <Icon className="h-4 w-4" />
                        {tab.label}
                      </Button>
                    );
                  })}
                </div>
              </div>

              <Separator className="my-4" />

              {/* Page Managers */}
              <div>
                <h3 className="text-xs font-bold text-slate-500 dark:text-slate-400 mb-3 px-2 tracking-wider">PAGE MANAGERS</h3>
                <div className="flex flex-wrap gap-2">
                  {tabs.filter(tab => tab.section === 'pages').map((tab) => {
                    const Icon = tab.icon;
                    const isActive = activeTab === tab.id;
                    
                    return (
                      <Button
                        key={tab.id}
                        variant={isActive ? "default" : "outline"}
                        size="sm"
                        onClick={() => setActiveTab(tab.id)}
                        className={`flex items-center gap-2 transition-all duration-200 ${
                          isActive 
                            ? 'bg-gradient-to-r from-slate-900 to-slate-700 dark:from-slate-100 dark:to-slate-300 text-white dark:text-slate-900 shadow-md border-0' 
                            : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100 hover:bg-slate-100 dark:hover:bg-slate-800 hover:border-slate-300 dark:hover:border-slate-600'
                        }`}
                      >
                        <Icon className="h-4 w-4" />
                        {tab.label}
                      </Button>
                    );
                  })}
                </div>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <div className="bg-gradient-to-br from-slate-50 to-white dark:from-slate-900 dark:to-slate-950">
              <ActiveComponent />
            </div>
          </CardContent>
        </Card>
        </div>
      </div>
    </CMSNavigationProvider>
  );
};

export default ContentManagement;
